import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import type { StaffDirectoryEntry } from '../../../types/staffManager';
import type { StaffRosterEntry } from '../../../types/staff';
import { StaffAvatar } from '../StaffAvatar';
import type { StaffNoteSubjectRef } from './StaffNoteModal';
import { cn } from '../../../utils/cn';

export interface SubjectOption extends StaffNoteSubjectRef {
  key: string;
  display_name: string;
  handle: string | null;
  discord_id: string | null;
}

interface StaffNoteSubjectPickerProps {
  directory: StaffDirectoryEntry[];
  roster: StaffRosterEntry[];
  value: SubjectOption | null;
  onChange: (subject: SubjectOption | null) => void;
}

/** Merges the Tesseract directory and the steward roster into one searchable list of staff subjects. */
export function buildSubjectOptions(directory: StaffDirectoryEntry[], roster: StaffRosterEntry[]): SubjectOption[] {
  const byDiscord = new Map<string, SubjectOption>();
  for (const r of roster) {
    byDiscord.set(r.discord_id, { key: `d:${r.discord_id}`, display_name: r.display_name, handle: r.username, discord_id: r.discord_id, uuid: null, user_id: null });
  }
  const options: SubjectOption[] = [];
  for (const e of directory) {
    const existing = e.discord_id ? byDiscord.get(e.discord_id) : undefined;
    if (existing) {
      existing.uuid = e.minecraft_uuid;
      existing.user_id = e.user_id;
      existing.handle = existing.handle ?? e.discord_username ?? e.username;
      continue;
    }
    options.push({
      key: `u:${e.user_id}`,
      display_name: e.minecraft_username ?? e.username,
      handle: e.discord_username ?? e.username,
      discord_id: e.discord_id,
      uuid: e.minecraft_uuid,
      user_id: e.user_id,
    });
  }
  return [...byDiscord.values(), ...options].sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export const StaffNoteSubjectPicker = ({ directory, roster, value, onChange }: StaffNoteSubjectPickerProps) => {
  const [query, setQuery] = useState('');
  const options = useMemo(() => buildSubjectOptions(directory, roster), [directory, roster]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 12);
    return options
      .filter((o) => o.display_name.toLowerCase().includes(q) || (o.handle ?? '').toLowerCase().includes(q) || (o.discord_id ?? '').includes(q) || (o.uuid ?? '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [options, query]);

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
        <input className="glass-input w-full pl-10" placeholder="Search staff by name, handle, Discord ID or UUID…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search staff" />
      </div>
      <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-glass-200/15 divide-y divide-glass-200/10" role="listbox" aria-label="Staff members">
        {filtered.length === 0 && <li className="px-3 py-3 text-xs text-glass-500">No staff match.</li>}
        {filtered.map((o) => {
          const active = value?.key === o.key;
          return (
            <li key={o.key}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => onChange(active ? null : o)}
                className={cn('w-full text-left px-3 py-2 flex items-center gap-3 transition-colors touch-manipulation', active ? 'bg-accent-500/15' : 'hover:bg-glass-200/10')}
              >
                {o.discord_id ? <StaffAvatar discordId={o.discord_id} name={o.display_name} size="sm" /> : <span className="w-8 h-8 rounded-lg bg-glass-100/10" aria-hidden="true" />}
                <span className="min-w-0 flex-1">
                  <span className={cn('block text-sm font-semibold truncate', active ? 'text-accent-200' : 'text-white')}>{o.display_name}</span>
                  <span className="block text-[11px] font-mono text-glass-500 truncate">{o.handle ? `@${o.handle}` : o.discord_id ?? o.uuid ?? o.user_id}</span>
                </span>
                {active && <Check className="w-4 h-4 text-accent-300 flex-shrink-0" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
